// The sound palette. Each preset is plain data; the instrument classes in
// synth.ts, fm.ts and sampler.ts turn it into Web Audio graphs.

export interface Env {
  a: number;
  d: number;
  s: number;
  r: number;
}

export interface OscSpec {
  wave: OscillatorType;
  /** Harmonic amplitudes for a custom wave (index 0 = fundamental). */
  partials?: number[];
  octave?: number;
  detune?: number;
  level: number;
}

interface PresetBase {
  id: string;
  name: string;
  /** One-line description shown under the name on the screen. */
  blurb: string;
  /** Octave offset applied to every note, e.g. -1 for basses. */
  octave?: number;
  gain: number;
}

export interface SamplerPreset extends PresetBase {
  kind: 'sampler';
  attack: number;
  release: number;
  /** Lowpass cutoff in Hz at full velocity; softer notes are darker. */
  brightness: number;
  highShelf?: { freq: number; gain: number };
  /** Layer a second, detuned copy for a chorused, glassy shimmer. */
  doubleCents?: number;
}

export interface SynthPreset extends PresetBase {
  kind: 'synth';
  osc: OscSpec[];
  noise?: number;
  filter: {
    type: BiquadFilterType;
    cutoff: number;
    q: number;
    /** Envelope depth in octaves above the cutoff. */
    env: number;
    keyTrack: number;
    /** 0..1 how much velocity scales the filter envelope. */
    velocity: number;
  };
  filterEnv: Env;
  amp: Env;
  lfo?: { rate: number; pitch: number; filter: number };
  mono?: boolean;
  glide?: number;
  /** Small random per-voice pitch drift in cents, for analog warmth. */
  drift?: number;
}

export interface FmPair {
  carrier: number;
  modulator: number;
  index: number;
  /** Seconds for the modulation index to decay to `sustainIndex`. */
  decay: number;
  sustainIndex: number;
  level: number;
}

export interface FmPreset extends PresetBase {
  kind: 'fm';
  pairs: FmPair[];
  amp: Env;
  vibrato?: { rate: number; cents: number };
}

export type Preset = SamplerPreset | SynthPreset | FmPreset;

const organ = [1, 0.8, 0.6, 0.5, 0, 0.35, 0, 0.3];

export const PRESETS: Preset[] = [
  {
    id: 'grand-piano',
    kind: 'sampler',
    name: 'Grand Piano',
    blurb: 'concert grand, warm and honest',
    attack: 0.002,
    release: 0.45,
    brightness: 16000,
    gain: 2.0,
  },
  {
    id: 'house-piano',
    kind: 'sampler',
    name: 'House Piano',
    blurb: 'bright late-80s rave keys',
    attack: 0.001,
    release: 0.18,
    brightness: 19000,
    highShelf: { freq: 2600, gain: 8 },
    doubleCents: 9,
    gain: 2.05,
  },
  {
    id: 'felt-piano',
    kind: 'sampler',
    name: 'Felt Piano',
    blurb: 'soft hammers, late-night lo-fi',
    attack: 0.012,
    release: 1.1,
    brightness: 2200,
    gain: 1.8,
  },
  {
    id: 'electric-piano',
    kind: 'fm',
    name: 'Electric Piano',
    blurb: 'glassy FM tines',
    pairs: [
      { carrier: 1, modulator: 1, index: 2.2, decay: 1.4, sustainIndex: 0.35, level: 1 },
      { carrier: 1, modulator: 14, index: 1.6, decay: 0.12, sustainIndex: 0, level: 0.25 },
    ],
    amp: { a: 0.002, d: 2.6, s: 0.0, r: 0.35 },
    vibrato: { rate: 4.8, cents: 4 },
    gain: 0.215,
  },
  {
    id: 'poly-strings',
    kind: 'synth',
    name: 'Poly Strings',
    blurb: 'ensemble strings, slow bloom',
    osc: [
      { wave: 'sawtooth', detune: -9, level: 0.5 },
      { wave: 'sawtooth', detune: 9, level: 0.5 },
      { wave: 'sawtooth', octave: 1, detune: 3, level: 0.18 },
    ],
    filter: { type: 'lowpass', cutoff: 2400, q: 0.6, env: 0.6, keyTrack: 0.4, velocity: 0.3 },
    filterEnv: { a: 0.6, d: 1.2, s: 0.5, r: 1.2 },
    amp: { a: 0.45, d: 0.4, s: 0.9, r: 1.3 },
    lfo: { rate: 5.2, pitch: 6, filter: 0 },
    drift: 4,
    gain: 0.39,
  },
  {
    id: 'glass-pad',
    kind: 'synth',
    name: 'Glass Pad',
    blurb: 'airy, shimmering, endless',
    osc: [
      { wave: 'triangle', detune: -6, level: 0.6 },
      { wave: 'sawtooth', detune: 7, level: 0.25 },
      { wave: 'sine', octave: 1, level: 0.3 },
    ],
    noise: 0.015,
    filter: { type: 'lowpass', cutoff: 900, q: 3, env: 2.2, keyTrack: 0.5, velocity: 0.4 },
    filterEnv: { a: 1.4, d: 2.5, s: 0.35, r: 2.0 },
    amp: { a: 0.9, d: 1.0, s: 0.85, r: 2.4 },
    lfo: { rate: 0.35, pitch: 3, filter: 500 },
    drift: 5,
    gain: 0.47,
  },
  {
    id: 'analog-brass',
    kind: 'synth',
    name: 'Analog Brass',
    blurb: 'punchy poly brass stabs',
    osc: [
      { wave: 'sawtooth', detune: -5, level: 0.5 },
      { wave: 'sawtooth', detune: 5, level: 0.5 },
    ],
    filter: { type: 'lowpass', cutoff: 520, q: 1.2, env: 3.1, keyTrack: 0.6, velocity: 0.7 },
    filterEnv: { a: 0.04, d: 0.35, s: 0.45, r: 0.3 },
    amp: { a: 0.02, d: 0.3, s: 0.85, r: 0.28 },
    lfo: { rate: 5.5, pitch: 3, filter: 0 },
    drift: 3,
    gain: 0.4,
  },
  {
    id: 'house-stab',
    kind: 'synth',
    name: 'Pluck',
    blurb: 'short, snappy, rhythmic',
    osc: [
      { wave: 'sawtooth', detune: -11, level: 0.5 },
      { wave: 'square', detune: 11, level: 0.3 },
    ],
    filter: { type: 'lowpass', cutoff: 380, q: 4, env: 4.2, keyTrack: 0.5, velocity: 0.8 },
    filterEnv: { a: 0.001, d: 0.22, s: 0.0, r: 0.2 },
    amp: { a: 0.001, d: 0.45, s: 0.0, r: 0.25 },
    drift: 2,
    gain: 0.67,
  },
  {
    id: 'acid-bass',
    kind: 'synth',
    name: 'Acid Bass',
    blurb: 'squelchy, resonant, sliding',
    octave: -2,
    osc: [{ wave: 'sawtooth', level: 0.7 }],
    filter: { type: 'lowpass', cutoff: 260, q: 14, env: 3.6, keyTrack: 0.3, velocity: 0.9 },
    filterEnv: { a: 0.002, d: 0.28, s: 0.12, r: 0.12 },
    amp: { a: 0.002, d: 0.6, s: 0.75, r: 0.08 },
    mono: true,
    glide: 0.07,
    gain: 0.42,
  },
  {
    id: 'sub-bass',
    kind: 'synth',
    name: 'Sub Bass',
    blurb: 'round and deep',
    octave: -2,
    osc: [
      { wave: 'sine', level: 0.9 },
      { wave: 'triangle', octave: 1, level: 0.18 },
    ],
    filter: { type: 'lowpass', cutoff: 900, q: 0.5, env: 0.3, keyTrack: 0, velocity: 0.2 },
    filterEnv: { a: 0.002, d: 0.2, s: 0.5, r: 0.15 },
    amp: { a: 0.004, d: 0.3, s: 0.92, r: 0.14 },
    mono: true,
    glide: 0.03,
    gain: 0.43,
  },
  {
    id: 'square-lead',
    kind: 'synth',
    name: 'Square Lead',
    blurb: 'singing mono lead with glide',
    osc: [
      { wave: 'square', detune: -4, level: 0.45 },
      { wave: 'square', detune: 4, octave: -1, level: 0.25 },
    ],
    filter: { type: 'lowpass', cutoff: 1800, q: 2, env: 1.4, keyTrack: 0.6, velocity: 0.5 },
    filterEnv: { a: 0.01, d: 0.5, s: 0.5, r: 0.3 },
    amp: { a: 0.01, d: 0.2, s: 0.9, r: 0.22 },
    lfo: { rate: 5.6, pitch: 9, filter: 0 },
    mono: true,
    glide: 0.09,
    gain: 0.46,
  },
  {
    id: 'drawbar-organ',
    kind: 'synth',
    name: 'Drawbar Organ',
    blurb: 'tonewheel organ, gospel to house',
    osc: [{ wave: 'custom', partials: organ, level: 0.6 }],
    filter: { type: 'lowpass', cutoff: 7000, q: 0.5, env: 0, keyTrack: 0, velocity: 0 },
    filterEnv: { a: 0.001, d: 0.1, s: 1, r: 0.05 },
    amp: { a: 0.006, d: 0.05, s: 1, r: 0.07 },
    lfo: { rate: 6.4, pitch: 7, filter: 0 },
    gain: 0.37,
  },
  {
    id: 'crystal-bells',
    kind: 'fm',
    name: 'Crystal Bells',
    blurb: 'shimmering FM chimes',
    pairs: [
      { carrier: 1, modulator: 3.5, index: 3.2, decay: 2.2, sustainIndex: 0.2, level: 1 },
      { carrier: 2, modulator: 5.04, index: 1.2, decay: 0.8, sustainIndex: 0, level: 0.35 },
    ],
    amp: { a: 0.001, d: 3.4, s: 0, r: 2.2 },
    gain: 0.2,
  },
];

export function presetIndex(id: string): number {
  const i = PRESETS.findIndex((p) => p.id === id);
  return i < 0 ? 0 : i;
}
